import { useEffect, useRef, useState } from 'react';

import type { SvgIcon } from '@rover/types/src/SvgIcon';
import { Box, Flex } from '@rover/kibble/core';
import FilterChipGroup from '@rover/kibble/patterns/chips/FilterChipGroup/FilterChipGroup';
import type { CatalogIcon } from '@rover/react-lib/src/pages/shared/serviceCatalog/types';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { ServiceCategoryTabClickEvent } from '../../utilities/analyticsEvents';

import ServiceCard from './ServiceCard';

interface OverviewService {
  id: string;
  name: string;
  location: string | null;
  for: string;
  description: string;
  cta: string;
  imageUrl: string;
  serviceLandingUrlSlug?: string;
  href: string;
  icon?: CatalogIcon;
}

interface OverviewTab {
  id: string;
  label: string;
  services: OverviewService[];
  icon?: SvgIcon;
}

type Props = {
  defaultActiveTabId?: string;
  services: OverviewTab[];
};

const ServiceCategoryTabs = ({ defaultActiveTabId, services }: Props): JSX.Element => {
  const tabsWithServices = services.filter((tab) => tab.services.length > 0);
  const [activeTab, setActiveTab] = useState<string>(
    defaultActiveTabId && tabsWithServices.some((tab) => tab.id === defaultActiveTabId)
      ? defaultActiveTabId
      : tabsWithServices[0]?.id
  );
  const activeTabData = tabsWithServices.find((tab) => tab.id === activeTab);
  const isMountedRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
  }, []);

  const handleTabChange = (newValue: Array<string | number> | string | number | null): void => {
    if (newValue === null) return;
    const newTabId = String(newValue);
    if (newTabId === activeTab) return;
    if (!isMountedRef.current) {
      setActiveTab(newTabId);
      return;
    }

    setActiveTab(newTabId);
    emitAnalyticsEvent(new ServiceCategoryTabClickEvent({ tabId: newTabId }));
  };

  return (
    <Flex justifyContent="flex-start" width="100%" flexDirection="column" gap="2x">
      <Flex justifyContent="flex-start" alignItems="center" width="100%" gap="2x">
        <FilterChipGroup
          groupLabel="Service categories"
          showLabel={false}
          options={tabsWithServices.map((tab) => ({
            id: tab.id,
            label: tab.label,
            value: tab.id,
            icon: tab.icon,
          }))}
          canSelectMultiple={false}
          isDeselectable={false}
          selectedValues={[activeTab]}
          shouldWrap
          size="small"
          onChange={handleTabChange}
          selectedIcon={null}
        />
      </Flex>
      <Box>
        {activeTabData && (
          <Flex flexDirection="column" gap="8x">
            {activeTabData.services.map((service) => (
              <ServiceCard
                key={service.id}
                name={service.name}
                location={service.location}
                for={service.for}
                description={service.description}
                cta={service.cta}
                imageUrl={service.imageUrl}
                serviceType={service.id}
                icon={service.icon}
                href={service.href}
              />
            ))}
          </Flex>
        )}
      </Box>
    </Flex>
  );
};

export default ServiceCategoryTabs;
